import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexto/AuthContexto';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, X, Crown, Sparkles, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const COLORES = {
  slate: { border: 'border-slate-700', accent: 'text-slate-300', btn: 'bg-slate-700 hover:bg-slate-600 text-slate-100', glow: '' },
  cyan: { border: 'border-cyan-500', accent: 'text-cyan-400', btn: 'bg-cyan-500 hover:bg-cyan-600 text-black', glow: 'shadow-[0_0_40px_rgba(6,182,212,0.25)]' },
  amber: { border: 'border-amber-500', accent: 'text-amber-400', btn: 'bg-amber-500 hover:bg-amber-600 text-black', glow: 'shadow-[0_0_40px_rgba(245,158,11,0.25)]' },
};

export default function Planes() {
  const [planes, setPlanes] = useState([]);
  const [features, setFeatures] = useState({});
  const [miPlan, setMiPlan] = useState(null);
  const [esSucursal, setEsSucursal] = useState(false);
  const [planPendiente, setPlanPendiente] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [seleccionando, setSeleccionando] = useState('');
  const { usuario } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([
      api.get('/planes/publicos').then(r => r.data),
      usuario ? api.get('/mi-plan').then(r => r.data).catch(() => null) : Promise.resolve(null),
    ]).then(([pub, mine]) => {
      setPlanes(pub.planes);
      setFeatures(pub.features_catalogo);
      setMiPlan(mine?.plan?.id || null);
      setEsSucursal(!!mine?.es_sucursal);
      setPlanPendiente(mine?.plan_pendiente || null);
    }).catch(() => toast.error('Error cargando planes'))
      .finally(() => setCargando(false));
  }, [usuario]);

  const elegirPlan = async (planId) => {
    if (!usuario) { navigate('/registro'); return; }
    if (usuario.rol !== 'propietario') { toast.error('Solo el propietario puede cambiar el plan'); return; }
    if (esSucursal) {
      toast.error('Las sucursales heredan el plan del corporativo. Cambia el plan desde la cuenta principal.');
      return;
    }
    setSeleccionando(planId);
    try {
      const r = await api.put('/mi-plan', { plan_id: planId });
      if (r.data.aplicado === 'proxima_renovacion') {
        toast.success(r.data.mensaje);
        setPlanPendiente(r.data.plan_pendiente);
      } else {
        toast.success('Plan actualizado. Ve a Mi Suscripción para completar el pago.');
        setMiPlan(planId);
        setTimeout(() => navigate('/suscripcion'), 800);
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al cambiar plan'); }
    finally { setSeleccionando(''); }
  };

  if (cargando) return <div className="min-h-screen bg-slate-950 flex items-center justify-center">
    <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
  </div>;

  return (
    <div className="min-h-screen bg-slate-950 py-12 px-4" data-testid="planes-page">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          {planes.some(p => p.promo_visible) && (
            <div className="inline-flex items-center gap-2 bg-amber-500/10 text-amber-400 px-3 py-1 rounded-full text-xs font-medium mb-3">
              <Sparkles className="w-3.5 h-3.5" /> Promoción activa
            </div>
          )}
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-50 font-barlow uppercase tracking-tight">Elige tu plan</h1>
          <p className="text-slate-400 mt-3 max-w-xl mx-auto">
            {planes.some(p => p.promo_visible)
              ? 'Precios especiales los primeros meses. Sin permanencia, cancela cuando quieras.'
              : 'Sin permanencia, cancela cuando quieras.'}
          </p>
        </div>

        {esSucursal && (
          <div className="max-w-2xl mx-auto mb-6 bg-amber-500/10 border border-amber-800 rounded-sm p-4 text-center" data-testid="sucursal-warning">
            <p className="text-sm text-amber-300 font-medium">Esta es una sucursal</p>
            <p className="text-xs text-slate-400 mt-1">
              Las sucursales heredan el plan del corporativo. El cambio de plan debe hacerse desde la cuenta principal.
            </p>
          </div>
        )}

        {planPendiente && (
          <div className="max-w-2xl mx-auto mb-6 bg-cyan-500/10 border border-cyan-800 rounded-sm p-4 text-center" data-testid="plan-pendiente-banner">
            <p className="text-sm text-cyan-300 font-medium">Cambio programado</p>
            <p className="text-xs text-slate-400 mt-1">
              Tu plan cambiará a <span className="text-cyan-400 font-bold">{planPendiente.nombre}</span> en la próxima renovación.
            </p>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6">
          {planes.map(plan => {
            const c = COLORES[plan.color] || COLORES.cyan;
            const esMiPlan = miPlan === plan.id;
            const verPromo = plan.promo_visible !== false && plan.precio_promo < plan.precio_regular;
            const ahorroPorc = verPromo ? Math.round((1 - plan.precio_promo / plan.precio_regular) * 100) : 0;
            return (
              <Card key={plan.id}
                className={`bg-slate-900/60 ${c.border} border-2 relative ${plan.destacado ? c.glow : ''} ${plan.destacado ? 'md:-mt-4' : ''}`}
                data-testid={`plan-card-${plan.id}`}>
                {plan.destacado && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <Badge className="bg-cyan-500 text-black font-bold text-xs px-3 py-1 shadow-lg">
                      <Crown className="w-3 h-3 mr-1" /> {plan.cta || 'MÁS POPULAR'}
                    </Badge>
                  </div>
                )}
                <CardContent className="p-6 space-y-5">
                  <div>
                    <h3 className={`text-2xl font-bold font-barlow uppercase ${c.accent}`}>{plan.nombre}</h3>
                    <p className="text-xs text-slate-500 mt-1 h-8">{plan.descripcion}</p>
                  </div>

                  <div className="border-b border-slate-800 pb-4">
                    {verPromo ? (
                      <>
                        <div className="flex items-baseline gap-2">
                          <span className="text-slate-600 text-lg line-through">${plan.precio_regular}</span>
                          <Badge className="bg-red-500/20 text-red-400 border-red-800 text-[10px]">-{ahorroPorc}%</Badge>
                        </div>
                        <div className="flex items-baseline gap-1 mt-1">
                          <span className="text-4xl font-bold text-slate-50 font-mono">${plan.precio_promo}</span>
                          <span className="text-slate-500 text-sm">{plan.moneda}/mes</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">
                          Primeros {plan.meses_promo} meses, luego ${plan.precio_regular}/mes.
                        </p>
                      </>
                    ) : (
                      <div className="flex items-baseline gap-1">
                        <span className="text-4xl font-bold text-slate-50 font-mono">${plan.precio_regular}</span>
                        <span className="text-slate-500 text-sm">{plan.moneda}/mes</span>
                      </div>
                    )}
                    {plan.id === 'multisucursal' && plan.precio_por_sucursal_extra && (
                      <p className="text-[11px] text-amber-400 mt-1">
                        + ${plan.precio_por_sucursal_extra}/mes por sucursal adicional
                      </p>
                    )}
                  </div>

                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-2">Incluye</p>
                    <ul className="space-y-1.5">
                      {(plan.features || []).slice(0, 10).map(fk => (
                        <li key={fk} className="flex items-start gap-2 text-xs text-slate-300">
                          <Check className={`w-3.5 h-3.5 ${c.accent} shrink-0 mt-0.5`} />
                          <span>{features[fk] || fk}</span>
                        </li>
                      ))}
                      {(plan.features || []).length > 10 && (
                        <li className="text-[11px] text-slate-500 pl-5">+{plan.features.length - 10} funciones más</li>
                      )}
                    </ul>

                    {/* Bloqueadas visibles como upsell */}
                    {Object.keys(features).filter(fk => !plan.features?.includes(fk)).slice(0, 3).map(fk => (
                      <div key={fk} className="flex items-start gap-2 text-xs text-slate-600 mt-1.5">
                        <X className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                        <span className="line-through">{features[fk]}</span>
                      </div>
                    ))}
                  </div>

                  <Button onClick={() => elegirPlan(plan.id)}
                    disabled={seleccionando === plan.id || esMiPlan || esSucursal}
                    className={`w-full font-bold rounded-sm h-11 ${c.btn}`}
                    data-testid={`elegir-plan-${plan.id}-btn`}>
                    {esSucursal ? 'Solo desde cuenta principal' :
                     esMiPlan ? 'Tu plan actual' : seleccionando === plan.id ? 'Procesando...' :
                      <>Elegir {plan.nombre} <ArrowRight className="w-4 h-4 ml-2" /></>}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="text-center mt-10 space-y-2">
          <p className="text-slate-500 text-sm">🔒 Pago seguro con MercadoPago o Stripe · Cancela cuando quieras</p>
          <p className="text-slate-600 text-xs">¿Tienes dudas? Contáctanos en <span className="text-cyan-400">soporte@tallerespro.com</span></p>
        </div>
      </div>
    </div>
  );
}
